import React, { useState } from 'react';
import { HowCalculated, usePopover, Popover } from '../MetricsUI';

/*
 * Chart primitives for the cohort pages. Hand-rolled, no chart library — the
 * same choice the design prototype made, and the app has no charting dependency.
 *
 * Bars, dots, rules and labels are HTML/CSS rather than SVG. An SVG stretched to
 * a fluid container with preserveAspectRatio="none" distorts everything inside
 * it: circles become ellipses and text is squashed horizontally. Only the line
 * and its fill are SVG, because a path is the one thing that survives that
 * stretching intact.
 */

const NAVY = '#071146';
const ROSE = '#e11d48';

export const SERIES = {
  score: { bar: 'bg-lavender', band: 'bg-lavender/10' },
  improvement: { bar: 'bg-emerald-600', band: 'bg-emerald-600/10' },
};

/* ------------------------------------------------------------------ *
 * Histogram
 * ------------------------------------------------------------------ */

/**
 * Distribution with the median and mean drawn ON the chart.
 *
 * The two rules are the point. Where they sit apart, the mean is being dragged
 * by a tail — one very strong or very weak person in a small org — and quoting
 * the average alone would mislead. The shaded band is the middle 50%.
 *
 * Hovering a bar lists the people in it, and each name is clickable.
 */
export function Histogram({
  histogram,
  stat,
  unit = '%',
  series = 'score',
  members = [],
  valueOf,
  onOpenLearner,
  onOpen,
  height = 190,
}) {
  const openItem = onOpen || onOpenLearner;
  /*
   * Two states, not one.
   *
   * `hover` previews; `pinned` survives the pointer leaving. The people list
   * renders BELOW the chart — it has to, there is no room inside a 20px bar —
   * so on hover alone the names vanished the moment you moved towards them and
   * could never be clicked. Clicking a bar pins the list until it is dismissed.
   */
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const shown = pinned || hover;
  const s = SERIES[series] || SERIES.score;

  if (!histogram || !stat || stat.n === 0) {
    return <p className="py-10 text-center text-sm italic text-gray-400">No data yet</p>;
  }

  const bins = histogram.bins;
  const maxN = Math.max(1, ...bins.map((b) => b.n));
  const lo = bins[0].from;
  const hi = bins[bins.length - 1].to;
  const span = hi - lo || 1;
  const pos = (v) => `${((v - lo) / span) * 100}%`;

  // Which people fall in a bin, for the hover list. Top bin is inclusive of hi.
  const peopleIn = (b, isLast) =>
    (members || [])
      .map((m) => ({ m, v: valueOf ? valueOf(m) : null }))
      .filter(({ v }) => v != null && v >= b.from && (isLast ? v <= b.to : v < b.to))
      .map(({ m, v }) => ({ ...m, value: v }));

  // Every other boundary, so the axis does not crowd.
  const ticks = bins.filter((_, i) => i % 2 === 0).map((b) => b.from).concat([hi]);

  return (
    <div>
      <div className="relative" style={{ height }}>
        {/* middle 50% */}
        {stat.p25 != null && stat.p75 != null && (
          <div
            className={`absolute top-0 bottom-0 ${s.band}`}
            style={{ left: pos(stat.p25), width: `${((stat.p75 - stat.p25) / span) * 100}%` }}
          />
        )}

        <div className="absolute inset-0 flex items-end gap-[2px]">
          {bins.map((b, i) => {
            const isLast = i === bins.length - 1;
            const h = (b.n / maxN) * 100;
            return (
              <div
                key={i}
                role={b.n ? 'button' : undefined}
                title={b.n ? 'Click to keep this list open' : undefined}
                className={`relative flex h-full flex-1 flex-col justify-end ${b.n ? 'cursor-pointer' : ''}`}
                onMouseEnter={() => b.n && setHover({ bin: b, people: peopleIn(b, isLast) })}
                onMouseLeave={() => setHover(null)}
                onClick={() =>
                  b.n &&
                  setPinned((p) =>
                    p && p.bin === b ? null : { bin: b, people: peopleIn(b, isLast) }
                  )
                }
              >
                {b.n > 0 && (
                  <span className="mb-0.5 text-center text-[11px] font-medium tabular-nums text-gray-400">
                    {b.n}
                  </span>
                )}
                <div
                  className={`${b.n ? s.bar : 'bg-gray-100'} rounded-t-sm transition-opacity ${
                    shown && shown.bin === b ? 'opacity-100 ring-2 ring-navy/40' : 'opacity-90'
                  }`}
                  style={{ height: `${Math.max(b.n ? 2 : 1, h)}%` }}
                />
              </div>
            );
          })}
        </div>

        {/* median: solid navy. mean: dashed rose. */}
        {stat.median != null && (
          <div className="absolute top-0 bottom-0 w-[2px]" style={{ left: pos(stat.median), background: NAVY }} />
        )}
        {stat.mean != null && (
          <div
            className="absolute top-0 bottom-0 w-0 border-l-2 border-dashed"
            style={{ left: pos(stat.mean), borderColor: ROSE }}
          />
        )}
      </div>

      <div className="mt-1 flex justify-between text-[11px] tabular-nums text-gray-400">
        {ticks.map((t) => (
          <span key={t}>{t}</span>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-[2px]" style={{ background: NAVY }} />
          median{' '}
          <b className="tabular-nums text-navy">
            {Math.round(stat.median)}
            {unit}
          </b>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-0 border-l-2 border-dashed" style={{ borderColor: ROSE }} />
          mean{' '}
          <b className="tabular-nums" style={{ color: ROSE }}>
            {Math.round(stat.mean)}
            {unit}
          </b>
        </span>
        <span className="text-gray-500">
          middle 50%{' '}
          <b className="tabular-nums text-navy">
            {Math.round(stat.p25)}–{Math.round(stat.p75)}
            {unit}
          </b>
        </span>
        <span className="ml-auto text-gray-400">n = {stat.n}</span>
      </div>

      {/* Fixed-height readout so the card does not jump as the pointer moves. */}
      <div className="mt-2 min-h-[34px]">
        {shown && shown.people.length > 0 ? (
          <NameChips
            label={`${shown.bin.from}–${shown.bin.to}${unit}`}
            items={shown.people}
            onOpen={openItem}
            unit={unit}
            onDismiss={pinned ? () => setPinned(null) : undefined}
          />
        ) : (
          <p className="text-[11px] text-gray-400">Click a bar to list the people in it.</p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * name chips — the hover lists, and clickable everywhere
 * ------------------------------------------------------------------ */

/*
 * An entity is anything a number can be broken down into — a person on the
 * manager page, an organisation on the admin console. Both shapes are accepted
 * so the same tiles, histograms and donuts serve both pages: the alternative was
 * a parallel set of org-flavoured components that would drift.
 */
const entity = (x) => ({
  id: x.id ?? x.userId ?? x.organizationId,
  label: x.label ?? x.displayLabel ?? x.name,
  value: x.value,
  /* Why this person is in the list. The attention chips carry "gone quiet,
     barely progressed" rather than a number, and dropping it would turn a list
     that explains itself into a list of bare names. */
  note: x.note,
  /*
   * Every region's id for this person.
   *
   * A person can hold a record in more than one region and each mints its own
   * id, so the id a row displays may belong to a region the viewer's server has
   * never heard of. The drill-down has to send them all or the server cannot
   * find out who was clicked — opening somebody from a chip answered 404 while
   * the same person opened fine from the table, which did send them.
   */
  userIds: (x.userIds || []).map((u) => (u && u.userId) || u).filter(Boolean),
});

export function NameChips({
  label,
  items,
  people, // legacy alias, still used by every manager-page call site
  onOpen,
  onOpenLearner, // legacy alias
  unit = '',
  max = 12,
  onDismiss,
}) {
  const list = (items || people || []).map(entity);
  const open = onOpen || onOpenLearner;
  /*
   * "+63 more" used to be plain grey text, which reads as a disabled control:
   * it looks clickable, says there is more, and does nothing. It is a button
   * that reveals the rest — the whole point of these chips is that a count is
   * only actionable if you can see who it counts, and that argument does not
   * stop at the twelfth name.
   */
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? list : list.slice(0, max);
  const hidden = list.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs">
      {label && <span className="mr-1 font-medium text-gray-500">{label}:</span>}
      {shown.map((p) => (
        <button
          key={p.id}
          onClick={() => open && open(p.id, p.label, p.userIds)}
          className="rounded border border-gray-200 px-1.5 py-0.5 text-navy hover:border-lavender hover:bg-indigo-50/50"
        >
          <b className="font-semibold text-navy">{p.label}</b>
          {p.value != null && (
            <span className="ml-1 tabular-nums text-gray-400">
              {Math.round(p.value)}
              {unit}
            </span>
          )}
          {p.note && <span className="ml-1 font-normal text-gray-500">— {p.note}</span>}
        </button>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="rounded border border-dashed border-gray-300 px-1.5 py-0.5 text-gray-500 hover:border-lavender hover:bg-indigo-50/50 hover:text-navy"
        >
          +{hidden} more
        </button>
      )}
      {expanded && list.length > max && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="rounded px-1.5 py-0.5 text-gray-400 hover:text-navy"
        >
          Show fewer
        </button>
      )}
      {onDismiss && (
        <button onClick={onDismiss} className="ml-1 text-gray-400 hover:text-navy" aria-label="Dismiss">
          &times;
        </button>
      )}
    </div>
  );
}

/**
 * A number that lists who it counts when hovered.
 *
 * Every count on this page is a claim about specific people, and "Regressing 2"
 * is only actionable if you can find out which two.
 */
export function CountTile({
  label,
  value,
  tone = 'default',
  hint,
  items,
  people, // legacy alias
  onOpen,
  onOpenLearner, // legacy alias
  unit = '',
  help,
  /* How many names the panel shows before "+N more". Default suits a short
     list; a long one like the attention list passes a smaller number so the
     panel opens readable rather than as a wall of names. */
  max = 12,
  width = 340,
}) {
  const list = items || people || [];
  const openItem = onOpen || onOpenLearner;
  const { open, toggle, close, anchorRef, panelRef } = usePopover();
  const tones = {
    default: 'text-navy',
    good: 'text-emerald-600',
    warn: 'text-amber-600',
    bad: 'text-rose-600',
    accent: 'text-lavender',
  };

  return (
    <div
      ref={anchorRef}
      className={`relative min-w-[150px] flex-1 rounded-lg bg-white p-4 shadow ${
        list.length ? 'cursor-pointer' : ''
      }`}
      /*
       * Click, not hover. On hover the panel opened whenever the pointer
       * crossed the tile and then covered the controls underneath it, so
       * reaching "View more details" meant racing a panel that appeared under
       * the cursor. It now opens only when asked and closes on an outside
       * click or Escape.
       */
      onClick={() => list.length && toggle()}
    >
      <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
        {help && <HowCalculated>{help}</HowCalculated>}
      </p>
      <p className={`mt-1 text-2xl font-bold ${tones[tone]}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-400">{hint}</p>}

      {/* Rendered through a portal: several of these tiles sit inside cards
          that clip their overflow, which cut the panel off at the card edge. */}
      <Popover anchorRef={anchorRef} panelRef={panelRef} open={open} width={width}>
        <NameChips items={list} onOpen={openItem} unit={unit} max={max} onDismiss={close} />
      </Popover>
      {list.length > 0 && !open && (
        <span className="absolute right-3 top-3 text-[10px] text-gray-300">{list.length}</span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * DotPlot — weekly active people
 * ------------------------------------------------------------------ */

/**
 * One dot per week, joined by a line, against a zero baseline.
 *
 * Zero-baselined on purpose: scaled to its own range, a wobble between 20 and 22
 * users would draw the same dramatic shape as a climb from 2 to 22.
 *
 * The dots are HTML, not SVG circles — the path is stretched to the container
 * width and circles inside it would render as ellipses.
 */
export function DotPlot({ points, height = 180, unit = '', emptyLabel = 'No activity recorded', of }) {
  const [hover, setHover] = useState(null);
  const vals = (points || []).map((p) => p.value);

  if (!vals.length) {
    return <p className="py-10 text-center text-sm italic text-gray-400">{emptyLabel}</p>;
  }

  const max = Math.max(1, ...vals);
  const xPct = (i) => (vals.length === 1 ? 50 : (i / (vals.length - 1)) * 100);
  const yPct = (v) => 100 - (v / max) * 100;

  const d = vals.map((v, i) => `${i ? 'L' : 'M'}${xPct(i).toFixed(2)},${yPct(v).toFixed(2)}`).join(' ');
  const area = `${d} L${xPct(vals.length - 1)},100 L${xPct(0)},100 Z`;
  const ticks = [max, Math.round(max / 2), 0];

  return (
    <div>
      <div className="flex">
        <div className="relative w-8 shrink-0" style={{ height }}>
          {ticks.map((t, i) => (
            <span
              key={t + '-' + i}
              className="absolute right-1 -translate-y-1/2 text-[11px] tabular-nums text-gray-400"
              style={{ top: `${(i / (ticks.length - 1)) * 100}%` }}
            >
              {t}
            </span>
          ))}
        </div>

        <div className="relative flex-1" style={{ height }}>
          {ticks.map((t, i) => (
            <div
              key={t + '-' + i}
              className="absolute left-0 right-0 border-t border-gray-100"
              style={{ top: `${(i / (ticks.length - 1)) * 100}%` }}
            />
          ))}

          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d={area} fill="#6c63ff" opacity="0.10" />
            <path d={d} fill="none" stroke="#6c63ff" strokeWidth="0.6" vectorEffect="non-scaling-stroke" />
          </svg>

          {points.map((p, i) => (
            <button
              key={p.label + '-' + i}
              onMouseEnter={() => setHover({ ...p, i })}
              onMouseLeave={() => setHover(null)}
              className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full p-1"
              style={{ left: `${xPct(i)}%`, top: `${yPct(p.value)}%` }}
              aria-label={`${p.label}: ${p.value}`}
            >
              <span
                className={`block rounded-full transition-all ${
                  hover && hover.i === i ? 'h-3 w-3 bg-navy' : 'h-2 w-2 bg-lavender'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="ml-8 mt-1 flex justify-between text-[11px] text-gray-400">
        <span>{points[0].label}</span>
        <span>{points[points.length - 1].label}</span>
      </div>

      <div className="mt-2 min-h-[20px] text-xs">
        {hover ? (
          <span>
            <b className="text-navy">{hover.label}</b>
            <span className="text-gray-600">
              {' '}
              — {hover.value}
              {unit} active{of ? ` of ${of}` : ''}
              {hover.minutes != null ? `, ${Math.round(hover.minutes / 60)}h logged` : ''}
            </span>
          </span>
        ) : (
          <span className="text-gray-400">Hover a dot for that week.</span>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * TargetBar
 * ------------------------------------------------------------------ */

/**
 * Renders an explanation rather than an empty bar when the value is unknown.
 * Seat use means nothing for an org with no seat count, and 26 of 33 have none —
 * a bar at 0% would read as "nobody is using it".
 */
export function TargetBar({ value, target, label, unit = '%', missingLabel, max }) {
  if (value == null) {
    return <p className="text-sm italic text-gray-400">{missingLabel || 'Not available'}</p>;
  }
  const ceiling = max || 100;
  const pct = Math.max(0, Math.min(100, (value / ceiling) * 100));
  const met = value >= target;

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-sm text-gray-600">{label}</span>
        <span className={`text-lg font-bold tabular-nums ${met ? 'text-emerald-600' : 'text-amber-600'}`}>
          {value}
          {unit}
        </span>
      </div>
      <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${met ? 'bg-emerald-500' : 'bg-amber-500'}`}
          style={{ width: `${pct}%` }}
        />
        <div
          className="absolute top-0 h-full w-0.5 bg-navy"
          style={{ left: `${Math.min(100, (target / ceiling) * 100)}%` }}
        />
      </div>
      <div className="mt-1 flex justify-between text-[10px]">
        <span className={met ? 'font-semibold text-emerald-600' : 'font-semibold text-amber-600'}>
          {met ? 'On target' : 'Below target'}
        </span>
        <span className="text-gray-400">
          target {target}
          {unit}
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Donut
 * ------------------------------------------------------------------ */

export function Donut({ slices, size = 150, centreValue, centreLabel, onOpenLearner, onOpen }) {
  const openItem = onOpen || onOpenLearner;
  const [hover, setHover] = useState(null);
  const [pinned, setPinned] = useState(null);
  const shown = pinned || hover;
  const total = slices.reduce((s, x) => s + x.value, 0);
  const R = 60;
  const C = 2 * Math.PI * R;

  let offset = 0;
  const arcs = slices.map((s) => {
    const len = total ? (s.value / total) * C : 0;
    const el = (
      <circle
        key={s.key || s.label}
        cx="80"
        cy="80"
        r={R}
        fill="none"
        stroke={s.color}
        strokeWidth="20"
        strokeDasharray={`${len} ${C - len}`}
        strokeDashoffset={-offset}
        transform="rotate(-90 80 80)"
      />
    );
    offset += len;
    return el;
  });

  return (
    <div>
      <div className="flex items-center gap-5">
        <div className="relative shrink-0" style={{ width: size, height: size }}>
          <svg width={size} height={size} viewBox="0 0 160 160">
            {total === 0 && <circle cx="80" cy="80" r={R} fill="none" stroke="#e5e7eb" strokeWidth="20" />}
            {arcs}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-bold tabular-nums text-navy">{centreValue}</span>
            {centreLabel && <span className="mt-0.5 text-[10px] text-gray-500">{centreLabel}</span>}
          </div>
        </div>

        <div className="flex-1 space-y-2">
          {slices.map((s) => (
            <div
              key={s.key || s.label}
              role={s.people && s.people.length ? 'button' : undefined}
              title={s.people && s.people.length ? 'Click to keep this list open' : undefined}
              className={`flex items-center gap-2.5 rounded px-1 py-0.5 hover:bg-gray-50 ${
                s.people && s.people.length ? 'cursor-pointer' : ''
              } ${shown === s ? 'bg-gray-50 ring-1 ring-gray-200' : ''}`}
              onMouseEnter={() => s.people && s.people.length && setHover(s)}
              onMouseLeave={() => setHover(null)}
              onClick={() => s.people && s.people.length && setPinned((p) => (p === s ? null : s))}
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: s.color }} />
              <span className="flex-1 text-sm text-gray-700">
                {s.label}
                {/* The definition sits with the label, in lighter grey. Behind a
                    tooltip it went unread, and "Power users: 12" tells a manager
                    nothing without knowing what qualifies. */}
                {s.definition && <span className="ml-1.5 text-xs text-gray-400">{s.definition}</span>}
              </span>
              <span className="text-sm font-semibold tabular-nums text-navy">{s.display ?? s.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-2 min-h-[34px]">
        {shown ? (
          <NameChips
            label={shown.label}
            items={shown.people || shown.items}
            onOpen={openItem}
            onDismiss={pinned ? () => setPinned(null) : undefined}
          />
        ) : (
          <p className="text-[11px] text-gray-400">Click a band to list the people in it.</p>
        )}
      </div>
    </div>
  );
}
